import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatINR } from "@/api/client";
import { Star, ShieldCheck, Truck, BadgeIndianRupee, Heart, ShoppingCart, Minus, Plus, MapPin, Zap } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/products/ProductCard";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import SeoHead, { breadcrumbJsonLd, productJsonLd } from "@/components/seo/SeoHead";
import ProductReviews from "@/components/reviews/ProductReviews";
import RichDescription from "@/components/products/RichDescription";

const ProductDetail = () => {
  const { id } = useParams();
  const { addItem, upsertItem } = useCart();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setData(null); setQty(1); setActiveImg(0);
    api.get(`/products/${id}`).then((r) => setData(r.data));
  }, [id]);

  const toggleWish = () => {
    api.post(`/wishlist/${id}`).then((r) => toast.success(r.data.action === "added" ? t("Added to wishlist") : t("Removed from wishlist"))).catch(() => toast.error(t("Please login to add to wishlist")));
  };

  if (!data) return <div className="container-fah py-20 text-center text-muted-foreground">{t("Loading product…")}</div>;
  const { product, similar, reviews } = data;
  const discount = product.mrp && product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  return (
    <div className="container-fah py-6 sm:py-10" data-testid="page-product-detail">
      <SeoHead
        title={product.name}
        description={`${product.name} by ${product.brand || product.seller_name} — ${product.description?.slice(0, 130) || "Premium agricultural product from a GST-verified seller on FarmersAgroHub."}`}
        path={`/product/${product.id}`}
        image={product.images?.[0] || undefined}
        jsonLd={[
          productJsonLd(product),
          breadcrumbJsonLd([
            { name: "Home", url: "/" },
            { name: product.category.replace(/-/g, " "), url: `/category/${product.category}` },
            { name: product.name, url: `/product/${product.id}` },
          ]),
        ]}
      />
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-primary">{t("Home")}</Link> / <Link to={`/category/${product.category}`} className="hover:text-primary capitalize">{t(product.category.replace(/-/g, " "))}</Link> / <span className="text-foreground">{t(product.name)}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* IMAGES */}
        <div>
          <div className="card-fah overflow-hidden aspect-[4/5] bg-muted/50">
            <img src={product.images[activeImg] || product.images[0]} alt={product.name} className="w-full h-full object-cover" data-testid="product-image-main" />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-14 h-[70px] rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-primary" : "border-border"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{product.brand}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1 text-balance" data-testid="product-title">{t(product.name)}</h1>

          <div className="mt-3 flex items-center gap-2">
            <div className="flex items-center gap-1 bg-primary text-primary-foreground px-2 py-0.5 rounded">
              <span className="text-sm font-semibold">{product.rating}</span>
              <Star className="w-3.5 h-3.5 fill-current" />
            </div>
            <span className="text-sm text-muted-foreground">{product.num_reviews} {t("ratings")}</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl sm:text-4xl font-bold">{formatINR(product.price)}</span>
            {product.mrp > product.price && <span className="text-lg text-muted-foreground line-through">{formatINR(product.mrp)}</span>}
            {discount > 0 && <span className="text-sm font-bold text-green-700">{discount}% {t("off")}</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{t("Inclusive of all taxes · per")} {t(product.unit)}</div>

          <div className="mt-6 card-fah p-4 bg-accent/40">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <span>{t("Sold by")} <Link to="#" className="font-semibold hover:underline">{product.seller_name}</Link> · <span className="text-green-700 font-semibold">{t("GST Verified")}</span></span>
            </div>
          </div>

          {/* Row 1: Quantity stepper + Wishlist */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-border rounded-lg">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:bg-muted" data-testid="qty-dec"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2 hover:bg-muted" data-testid="qty-inc"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 text-xs text-muted-foreground">{t("Pack Size")}: <span className="font-semibold text-foreground">{t(product.unit)}</span></div>
            <button onClick={toggleWish} className="p-3 border border-border rounded-lg hover:bg-muted" data-testid="btn-wishlist"><Heart className="w-5 h-5" /></button>
          </div>

          {/* Row 2: Add to Cart + Buy Now (60/40 on mobile, equal on desktop) */}
          <div className="mt-3 flex items-stretch gap-2 sm:gap-3">
            <button
              onClick={() => addItem(product, qty)}
              className="flex-[3] sm:flex-1 h-12 bg-secondary hover:opacity-90 text-secondary-foreground font-bold rounded-lg flex items-center justify-center gap-2 transition"
              data-testid="btn-add-cart"
            >
              <ShoppingCart className="w-5 h-5" /> {t("Add to Cart")}
            </button>
            <button
              onClick={() => {
                // Upsert (no duplicates) — sets qty to selected, doesn't add to existing qty
                upsertItem(product, qty);
                // If logged out, send to login with redirect back to checkout
                if (!user) {
                  navigate(`/login?next=${encodeURIComponent("/checkout")}`);
                } else {
                  navigate("/checkout");
                }
              }}
              className="flex-[2] sm:flex-1 h-12 text-white font-bold rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition"
              style={{ backgroundColor: "#16A34A" }}
              data-testid="btn-buy-now"
            >
              <Zap className="w-5 h-5 fill-white" /> {t("Buy Now")}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /> {t("Flat ₹50 shipping")}</div>
            <div className="flex items-center gap-2"><BadgeIndianRupee className="w-4 h-4 text-primary" /> {t("COD available")}</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {t("Pan-India delivery")}</div>
          </div>

          <div className="mt-7">
            <h3 className="font-display font-bold text-lg mb-2">{t("Description")}</h3>
            <RichDescription text={t(product.description)} />
          </div>

          <div className="mt-6 card-fah p-4">
            <h3 className="font-display font-bold text-base mb-3">{t("Specifications")}</h3>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t("Brand")}</dt><dd className="font-medium">{product.brand}</dd>
              <dt className="text-muted-foreground">{t("Pack Size")}</dt><dd className="font-medium">{t(product.unit)}</dd>
              <dt className="text-muted-foreground">{t("Category")}</dt><dd className="font-medium capitalize">{t(product.category.replace(/-/g, " "))}</dd>
              <dt className="text-muted-foreground">{t("Stock")}</dt><dd className="font-medium">{product.stock > 0 ? `${t("In stock")} (${product.stock})` : t("Out of stock")}</dd>
            </dl>
          </div>
        </div>
      </div>

      {/* REVIEWS — full ratings & reviews system */}
      <ProductReviews productId={product.id} product={product} />

      {/* SIMILAR */}
      {similar.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-5">{t("Similar Products")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {similar.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
